import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from 'styled-components';
import { supabase } from '../../../lib/supabaseClient';
import { ValidateEventDates } from '../../../utils/ValidateEvent';
import CustomEditor from '../../../Components/TextEditor/CustomEditor';
import AdressInput from '../../Atoms/AdressInput';

// Modern Design System Colors & Theme
const theme = {
  colors: {
    primary: '#0010ff',
    primaryLight: '#3344ff',
    primaryDark: '#0008cc',
    secondary: '#7C3AED',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    // Event Type Colors
    eventTypes: {
      sde: {
        primary: '#6366F1', // Indigo
        light: '#A5B4FC',
        dark: '#4338CA',
        background: '#EEF2FF'
      },
      sdo: {
        primary: '#8B5CF6', // Purple
        light: '#C4B5FD',
        dark: '#7C3AED',
        background: '#F3E8FF'
      },
      fd100: {
        primary: '#F59E0B', // Amber
        light: '#FCD34D',
        dark: '#D97706',
        background: '#FFFBEB'
      },
      design: {
        primary: '#10B981', // Emerald
        light: '#6EE7B7',
        dark: '#047857',
        background: '#ECFDF5'
      }
    },
    gray: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827'
    },
    text: {
      primary: '#111827',
      secondary: '#374151',
      tertiary: '#6B7280'
    },
    border: {
      primary: '#E5E7EB',
      focus: '#3B82F6'
    }
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    xxl: '3rem'
  },
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px'
  },
  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px rgba(0, 0, 0, 0.07)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.1)'
  }
};

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${theme.spacing.xl};
  background: ${theme.colors.gray[50]};
  min-height: calc(100vh - 200px);
`;

const PageHeader = styled.div`
  margin-bottom: ${theme.spacing.xxl};
`;

const BackButton = styled.button`
  background: white;
  border: 1px solid ${theme.colors.border.primary};
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};

  &:hover {
    background: ${theme.colors.gray[50]};
    border-color: ${theme.colors.gray[300]};
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.sm};
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${theme.colors.primary};
  margin: 0 0 ${theme.spacing.md} 0;
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: 1.1rem;
  margin: 0;
`;

const MainLayout = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${theme.spacing.xxl};
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.xl};
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

const Card = styled.div`
  background: white;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.sm};
  transition: all 0.2s ease;

  &:hover {
    box-shadow: ${theme.shadows.md};
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.gray[100]};
`;

const SectionIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.gray[100]};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  border: 2px solid ${theme.colors.gray[200]};
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.lg};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

const RequiredStar = styled.span`
  color: ${theme.colors.error};
`;

const Input = styled.input`
  padding: ${theme.spacing.md};
  border: 2px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  font-size: 0.875rem;
  transition: all 0.2s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: ${theme.colors.border.focus};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &[type="file"] {
    padding: ${theme.spacing.sm};
  }

  &::placeholder {
    color: ${theme.colors.gray[400]};
  }
`;

const Select = styled.select`
  padding: ${theme.spacing.md};
  border: 2px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  font-size: 0.875rem;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.border.focus};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const EditorWrapper = styled.div`
  border: 2px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  transition: all 0.2s ease;

  &:focus-within {
    border-color: ${theme.colors.border.focus};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  .quill {
    border: none;
  }
`;

const PreviewCard = styled(Card)`
  position: sticky;
  top: ${theme.spacing.xl};
  height: fit-content;
  border: 2px solid ${theme.colors.primary};
`;

const PreviewTitle = styled.h3`
  color: ${theme.colors.primary};
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: ${theme.spacing.lg};
  text-align: center;
`;

const PreviewItem = styled.div`
  margin-bottom: ${theme.spacing.md};
  
  strong {
    color: ${theme.colors.text.secondary};
    font-size: 0.875rem;
    display: block;
    margin-bottom: ${theme.spacing.xs};
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.025em;
  }
  
  span {
    color: ${theme.colors.text.primary};
    font-size: 1rem;
    font-weight: 500;
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  background: ${props => {
    switch(props.variant) {
      case 'primary': return theme.colors.primary;
      case 'success': return theme.colors.success;
      case 'warning': return theme.colors.warning;
      case 'error': return theme.colors.error;
      default: return theme.colors.gray[200];
    }
  }};
  color: ${props => props.variant === 'default' ? theme.colors.text.primary : 'white'};
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.lg};
  background: white;
  border-top: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg} ${theme.borderRadius.lg} 0 0;
  margin: ${theme.spacing.xl} -${theme.spacing.xl} -${theme.spacing.xl};
  box-shadow: ${theme.shadows.lg};
`;

const Button = styled.button`
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.md};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  ${props => {
    if (props.variant === 'outline') {
      return `
        background: white;
        color: ${theme.colors.text.primary};
        border-color: ${theme.colors.border.primary};
        
        &:hover:not(:disabled) {
          border-color: ${theme.colors.gray[300]};
          background: ${theme.colors.gray[50]};
        }
      `;
    }
    return `
      background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryLight} 100%);
      color: white;
      
      &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: ${theme.shadows.lg};
      }
    `;
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const Alert = styled.div`
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-size: 0.875rem;
  margin-bottom: ${theme.spacing.md};
  border-left: 4px solid;

  ${props => {
    switch(props.variant) {
      case 'error':
        return `
          background: ${theme.colors.gray[50]};
          color: ${theme.colors.error};
          border-color: ${theme.colors.error};
        `;
      case 'success':
        return `
          background: ${theme.colors.gray[50]};
          color: ${theme.colors.success};
          border-color: ${theme.colors.success};
        `;
      case 'warning':
        return `
          background: ${theme.colors.gray[50]};
          color: ${theme.colors.warning};
          border-color: ${theme.colors.warning};
        `;
      default:
        return `
          background: ${theme.colors.gray[50]};
          color: ${theme.colors.text.secondary};
          border-color: ${theme.colors.gray[300]};
        `;
    }
  }}
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Event Type Selection Components
const EventTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.lg};
  margin-top: ${theme.spacing.md};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
  
  @media (min-width: 1200px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const EventTypeCard = styled.div`
  padding: ${theme.spacing.lg};
  border: 2px solid ${props => props.selected ? theme.colors.eventTypes[props.eventType]?.primary : theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.selected ? theme.colors.eventTypes[props.eventType]?.background : 'white'};
  position: relative;
  
  &:hover {
    border-color: ${props => theme.colors.eventTypes[props.eventType]?.primary};
    background: ${props => theme.colors.eventTypes[props.eventType]?.background};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
  }

  ${props => props.selected && `
    box-shadow: 0 0 0 4px ${theme.colors.eventTypes[props.eventType]?.primary}20;
  `}
`;

const EventTypeIcon = styled.div`
  font-size: 2rem;
  margin-bottom: ${theme.spacing.md};
  text-align: center;
`;

const EventTypeName = styled.h3`
  color: ${props => props.selected ? theme.colors.eventTypes[props.eventType]?.primary : theme.colors.text.primary};
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 ${theme.spacing.sm} 0;
  text-align: center;
`;

const EventTypeDescription = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: 0.875rem;
  margin: 0;
  text-align: center;
  line-height: 1.4;
`;

const SelectedBadge = styled.div`
  position: absolute;
  top: ${theme.spacing.sm};
  right: ${theme.spacing.sm};
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => theme.colors.eventTypes[props.eventType]?.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: bold;
`;

// Enhanced Address Input Container
const AddressInputContainer = styled.div`
  .address-input {
    width: 100%;
  }
  
  /* Style the AdressInput to match our design system */
  input {
    padding: ${theme.spacing.md};
    border: 2px solid ${theme.colors.border.primary};
    border-radius: ${theme.borderRadius.md};
    font-size: 0.875rem;
    transition: all 0.2s ease;
    background: white;
    width: 100%;

    &:focus {
      outline: none;
      border-color: ${theme.colors.border.focus};
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    &::placeholder {
      color: ${theme.colors.gray[400]};
    }
  }
`;

// Enhanced Preview Components
const PreviewSection = styled.div`
  margin-bottom: ${theme.spacing.lg};
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.gray[50]};
  border-left: 4px solid ${props => theme.colors.eventTypes[props.eventType]?.primary || theme.colors.gray[300]};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${theme.colors.gray[200]};
  border-radius: 4px;
  overflow: hidden;
  margin: ${theme.spacing.lg} 0;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
  transition: width 0.3s ease;
  width: ${props => props.progress}%;
`;

// Media Preview Components
const MediaPreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const MediaPreviewItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const MediaPreviewImage = styled.img`
  width: 100%;
  height: 80px;
  object-fit: cover;
  border-radius: ${theme.borderRadius.md};
  border: 2px solid ${theme.colors.gray[200]};
`;

const MediaPreviewPlaceholder = styled.div`
  width: 100%;
  height: 80px;
  background: ${theme.colors.gray[100]};
  border: 2px dashed ${theme.colors.gray[300]};
  border-radius: ${theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.gray[500]};
  font-size: 0.75rem;
  text-align: center;
`;

const MediaFileName = styled.span`
  font-size: 0.75rem;
  color: ${theme.colors.text.secondary};
  font-weight: 500;
  word-break: break-all;
`;

const CreateEventModern = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const isEditMode = !!editId;

  const [loading, setLoading] = useState(false);
  const [loadingEvent, setLoadingEvent] = useState(false);
  const [error, setError] = useState('');
  const [eventType, setEventType] = useState('sde');
  const [descFR, setDescFR] = useState('');
  const [descEN, setDescEN] = useState('');

  // Form field states for reactive preview
  const [titleFR, setTitleFR] = useState('');
  const [titleEN, setTitleEN] = useState('');
  const [localisation, setLocalisation] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [mainDate, setMainDate] = useState('');
  const [onlineDate, setOnlineDate] = useState('');
  const [amOrPm, setAmOrPm] = useState('');
  const [inscriptionsStartDate, setInscriptionsStartDate] = useState('');
  const [inscriptionsEndDate, setInscriptionsEndDate] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingLogoPath, setExistingLogoPath] = useState(null);
  const [existingImagePath, setExistingImagePath] = useState(null);

  // SDE-specific fields
  const [popAOpen, setPopAOpen] = useState(true);
  const [popBOpen, setPopBOpen] = useState(true);
  const [slotDuration, setSlotDuration] = useState(20);
  const [maxCandidatures, setMaxCandidatures] = useState(15);
  const [sdeSlots, setSdeSlots] = useState([
    { id: 'slot1', label: '9h00 - 9h20', online: false },
    { id: 'slot2', label: '9h25 - 9h45', online: false },
    { id: 'slot3', label: '9h50 - 10h10', online: false },
    { id: 'slot4', label: '10h15 - 10h35', online: false },
    { id: 'slot5', label: '10h40 - 11h00', online: false },
    { id: 'slot6', label: '11h05 - 11h25', online: false },
    { id: 'slot7', label: '11h30 - 11h50', online: false },
  ]);

  // Load existing event in edit mode
  useEffect(() => {
    if (!editId) return;
    const loadEvent = async () => {
      setLoadingEvent(true);
      const { data: ev, error: evErr } = await supabase
        .from('events')
        .select('*')
        .eq('id', editId)
        .single();
      if (evErr || !ev) {
        setError("Événement introuvable.");
        setLoadingEvent(false);
        return;
      }
      setEventType(ev.type || 'sde');
      setTitleFR(ev.title_fr || '');
      setTitleEN(ev.title_en || '');
      setLocalisation(ev.location || '');
      setDescFR(ev.description_fr || '');
      setDescEN(ev.description_en || '');
      setMainDate(ev.event_start_date || '');
      setOnlineDate(ev.event_end_date ? ev.event_end_date.split('T')[0] : '');
      setInscriptionsStartDate(ev.inscriptions_start_date ? ev.inscriptions_start_date.split('T')[0] : '');
      setInscriptionsEndDate(ev.inscriptions_end_date ? ev.inscriptions_end_date.split('T')[0] : '');
      setExistingLogoPath(ev.logo_path || null);
      setExistingImagePath(ev.image_path || null);
      // Build preview URLs for existing images
      if (ev.logo_path) {
        const { data: logoUrl } = supabase.storage.from('event-images').getPublicUrl(ev.logo_path);
        setLogoPreview(logoUrl?.publicUrl || null);
      }
      if (ev.image_path) {
        const { data: imgUrl } = supabase.storage.from('event-images').getPublicUrl(ev.image_path);
        setImagePreview(imgUrl?.publicUrl || null);
      }
      // SDE config
      if (ev.pop_a_open !== undefined) setPopAOpen(ev.pop_a_open);
      if (ev.pop_b_open !== undefined) setPopBOpen(ev.pop_b_open);
      if (ev.sde_config) {
        if (ev.sde_config.slot_duration_minutes) setSlotDuration(ev.sde_config.slot_duration_minutes);
        if (ev.sde_config.max_candidatures) setMaxCandidatures(ev.sde_config.max_candidatures);
        if (ev.sde_config.slots) setSdeSlots(ev.sde_config.slots);
      }
      setLoadingEvent(false);
    };
    loadEvent();
  }, [editId]);

  const title_fr_ref = useRef(null);
  const title_en_ref = useRef(null);
  const main_date_ref = useRef(null);
  const online_date_ref = useRef(null);
  const am_or_pm_ref = useRef(null);
  const inscriptions_start_date_ref = useRef(null);
  const inscriptions_end_date_ref = useRef(null);
  const logo_path_ref = useRef(null);
  const image_path_ref = useRef(null);

  // Monitor address input changes for live preview
  useEffect(() => {
    const handleAddressChange = () => {
      // Check for the visible input that AdressInput renders
      const visibleInput = document.querySelector('input[placeholder*="Paris Expo"], input[placeholder*="Porte de Versailles"]');
      const hiddenInput = document.querySelector('input[name="localisation"]');
      const latInput = document.querySelector('input[name="latitude"]');
      const lngInput = document.querySelector('input[name="longitude"]');

      if (visibleInput && visibleInput.value !== localisation) {
        setLocalisation(visibleInput.value);
      } else if (hiddenInput && hiddenInput.value !== localisation) {
        setLocalisation(hiddenInput.value);
      }

      if (latInput && latInput.value !== latitude) {
        setLatitude(latInput.value);
      }

      if (lngInput && lngInput.value !== longitude) {
        setLongitude(lngInput.value);
      }
    };

    // Set up a more frequent polling for better responsiveness
    const interval = setInterval(handleAddressChange, 500);

    // Listen for various events that might indicate address changes
    const handleDocumentChange = (e) => {
      if (e.target.type === 'text' && (
          e.target.name === 'localisation' ||
          e.target.placeholder?.includes('Paris Expo') ||
          e.target.placeholder?.includes('Porte de Versailles')
      )) {
        setTimeout(() => {
          setLocalisation(e.target.value);
        }, 50);
      }
      if (e.target.name === 'latitude') {
        setLatitude(e.target.value);
      }
      if (e.target.name === 'longitude') {
        setLongitude(e.target.value);
      }
    };

    document.addEventListener('input', handleDocumentChange);
    document.addEventListener('change', handleDocumentChange);
    document.addEventListener('click', handleAddressChange); // For suggestion clicks

    return () => {
      clearInterval(interval);
      document.removeEventListener('input', handleDocumentChange);
      document.removeEventListener('change', handleDocumentChange);
      document.removeEventListener('click', handleAddressChange);
    };
  }, [localisation, latitude, longitude]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Get form data
    const formData = new FormData(e.target);

    const eventStartDate = mainDate || main_date_ref.current?.value;
    const eventEndDate = onlineDate || online_date_ref.current?.value;
    const registrationStartDate = inscriptionsStartDate || inscriptions_start_date_ref.current?.value;
    const registrationEndDate = inscriptionsEndDate || inscriptions_end_date_ref.current?.value;

    // Skip date validation for FD100 if optional date fields are empty
    const shouldValidateDates = eventType !== 'fd100' || (registrationStartDate && registrationEndDate);

    if (shouldValidateDates && !ValidateEventDates({
      type: eventType,
      main_date: eventStartDate,
      online_date: eventEndDate,
      inscriptions_start_date: registrationStartDate,
      inscriptions_end_date: registrationEndDate
    })) {
      setError("Les dates ne sont pas valides. Veuillez vérifier que les dates d'inscription précèdent les dates d'événement.");
      setLoading(false);
      return;
    }

    try {
      // Upload logo to Supabase Storage
      let logoPath = null;
      if (logoFile) {
        const logoExt = logoFile.name.split('.').pop();
        const logoFileName = `${Date.now()}-logo.${logoExt}`;
        const { data: logoData, error: logoError } = await supabase.storage
          .from('event-images')
          .upload(logoFileName, logoFile);

        if (logoError) {
          console.error('Error uploading logo:', logoError);
        } else {
          logoPath = logoData.path;
        }
      }

      // Upload main image to Supabase Storage
      let imagePath = null;
      if (imageFile) {
        const imageExt = imageFile.name.split('.').pop();
        const imageFileName = `${Date.now()}-image.${imageExt}`;
        const { data: imageData, error: imageError } = await supabase.storage
          .from('event-images')
          .upload(imageFileName, imageFile);

        if (imageError) {
          console.error('Error uploading image:', imageError);
        } else {
          imagePath = imageData.path;
        }
      }

      // Get location from form data
      const locationValue = formData.get('localisation');

      // Helper to convert empty strings to null for date fields
      const toNullableDate = (value) => (value && value.trim() !== '' ? value : null);

      // Build event data
      const eventData = {
        type: eventType,
        title_fr: titleFR || title_fr_ref.current?.value,
        title_en: titleEN || title_en_ref.current?.value,
        description_fr: descFR || null,
        description_en: descEN || null,
        location: locationValue || localisation || null,
        event_start_date: toNullableDate(eventStartDate),
        event_end_date: toNullableDate(eventEndDate),
        inscriptions_start_date: toNullableDate(registrationStartDate),
        inscriptions_end_date: toNullableDate(registrationEndDate),
        // SDE-specific fields
        ...((['sde', 'sdo'].includes(eventType)) && {
          pop_a_open: popAOpen,
          pop_b_open: popBOpen,
          online_date: toNullableDate(onlineDate),
          sde_config: {
            slot_duration_minutes: slotDuration,
            max_candidatures: maxCandidatures,
            slots: sdeSlots,
          },
        }),
      };

      // Only update image paths if new files were uploaded
      if (logoPath) {
        eventData.logo_path = logoPath;
      } else if (!isEditMode) {
        eventData.logo_path = null;
      }
      if (imagePath) {
        eventData.image_path = imagePath;
      } else if (!isEditMode) {
        eventData.image_path = null;
      }

      let result;
      if (isEditMode) {
        result = await supabase
          .from('events')
          .update(eventData)
          .eq('id', editId)
          .select();
      } else {
        result = await supabase
          .from('events')
          .insert([eventData])
          .select();
      }

      if (result.error) {
        console.error(`Error ${isEditMode ? 'updating' : 'creating'} event:`, result.error);
        setError(`Une erreur est survenue: ${result.error.message}`);
        setLoading(false);
        return;
      }

      console.log(`Event ${isEditMode ? 'updated' : 'created'} successfully:`, result.data);
      navigate("/admin/events");
    } catch (err) {
      console.error(err);
      setError("Une erreur est survenue lors de la création de l'événement. Veuillez réessayer.");
      setLoading(false);
    }
  };

  const getEventTypeInfo = (type) => {
    switch (type) {
      case 'sde':
        return {
          name: 'Speed Dating Espace',
          icon: '🏢',
          description: 'Rencontres entre designers et espaces commerciaux'
        };
      case 'sdo':
        return {
          name: 'Speed Dating Objet',
          icon: '🪑',
          description: 'Rencontres entre créateurs et éditeurs d\'objets'
        };
      case 'fd100':
        return {
          name: 'French Design 100',
          icon: '🏆',
          description: 'Concours annuel récompensant l\'excellence du design français'
        };
      case 'design':
        return {
          name: 'Design Event',
          icon: '🎨',
          description: 'Événements dédiés au design : semaines du design, expositions, conférences'
        };
      default:
        return {
          name: '',
          icon: '📋',
          description: ''
        };
    }
  };

  const getEventTypeName = () => {
    return getEventTypeInfo(eventType).name;
  };

  // Calculate form completion progress
  const calculateProgress = () => {
    const fields = [
      eventType,
      titleFR,
      titleEN,
      localisation,
      mainDate,
      onlineDate,
      amOrPm,
      inscriptionsStartDate,
      inscriptionsEndDate,
      descFR,
      descEN,
      logoFile,
      imageFile
    ];
    
    const completedFields = fields.filter(field => field && field !== '').length;
    return Math.round((completedFields / fields.length) * 100);
  };

  // Handle file uploads and create previews
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    setLogoFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setLogoPreview(null);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    setImageFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non défini';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Container>
      <PageHeader>
        <BackButton onClick={() => navigate("/admin/events")}>
          ← Retour aux événements
        </BackButton>
        <Title>{isEditMode ? 'Modifier l\'événement' : 'Créer un nouvel événement'}</Title>
        <Subtitle>
          {isEditMode ? 'Modifiez les informations de l\'événement' : 'Interface moderne pour la création d\'événements Le French Design'}
        </Subtitle>
      </PageHeader>

      {error && <Alert variant="error">{error}</Alert>}

      <MainLayout>
        <FormContainer>
          <form onSubmit={handleSubmit}>
            {/* Event Type Section */}
            <Card>
              <SectionHeader>
                <SectionIcon>🎯</SectionIcon>
                <SectionTitle>Type d'événement</SectionTitle>
              </SectionHeader>
              <Label>
                Choisissez le type d'événement <RequiredStar>*</RequiredStar>
              </Label>
              <EventTypeGrid>
                {['sde', 'sdo', 'fd100', 'design'].map((type) => {
                  const info = getEventTypeInfo(type);
                  return (
                    <EventTypeCard
                      key={type}
                      eventType={type}
                      selected={eventType === type}
                      onClick={() => setEventType(type)}
                    >
                      {eventType === type && <SelectedBadge eventType={type}>✓</SelectedBadge>}
                      <EventTypeIcon>{info.icon}</EventTypeIcon>
                      <EventTypeName selected={eventType === type} eventType={type}>
                        {info.name}
                      </EventTypeName>
                      <EventTypeDescription>{info.description}</EventTypeDescription>
                    </EventTypeCard>
                  );
                })}
              </EventTypeGrid>
            </Card>

            {/* Basic Information Section */}
            <Card>
              <SectionHeader>
                <SectionIcon>📝</SectionIcon>
                <SectionTitle>Informations générales</SectionTitle>
              </SectionHeader>
              <Grid>
                <FormGroup>
                  <Label htmlFor="title_fr">
                    Nom de l'événement (FR) <RequiredStar>*</RequiredStar>
                  </Label>
                  <Input 
                    id="title_fr"
                    type="text" 
                    ref={title_fr_ref} 
                    value={titleFR}
                    onChange={(e) => setTitleFR(e.target.value)}
                    required 
                    placeholder={`Nom du ${getEventTypeName()} en français`}
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="title_en">
                    Nom de l'événement (EN) <RequiredStar>*</RequiredStar>
                  </Label>
                  <Input 
                    id="title_en"
                    type="text" 
                    ref={title_en_ref} 
                    value={titleEN}
                    onChange={(e) => setTitleEN(e.target.value)}
                    required 
                    placeholder={`Nom du ${getEventTypeName()} en anglais`}
                  />
                </FormGroup>
              </Grid>
              <FormGroup style={{ marginTop: theme.spacing.lg }}>
                <Label htmlFor="localisation">
                  Localisation <RequiredStar>*</RequiredStar>
                </Label>
                <AddressInputContainer>
                  <AdressInput
                    adressInputName="localisation"
                    latitutdeInputName="latitude"
                    longitudeInputName="longitude"
                    placeholder="Ex: Paris Expo Porte de Versailles, Hall 5.2"
                    required={true}
                    adressDefaultValue={localisation}
                  />
                </AddressInputContainer>
              </FormGroup>
            </Card>

            {/* Descriptions Section */}
            <Card>
              <SectionHeader>
                <SectionIcon>📄</SectionIcon>
                <SectionTitle>Descriptions</SectionTitle>
              </SectionHeader>
              <FormGroup>
                <Label>Description FR</Label>
                <EditorWrapper>
                  <CustomEditor 
                    value={descFR}
                    onChange={setDescFR}
                  />
                </EditorWrapper>
              </FormGroup>
              <FormGroup>
                <Label>Description EN</Label>
                <EditorWrapper>
                  <CustomEditor 
                    value={descEN}
                    onChange={setDescEN}
                  />
                </EditorWrapper>
              </FormGroup>
            </Card>

            {/* Dates and Schedule Section */}
            <Card>
              <SectionHeader>
                <SectionIcon>📅</SectionIcon>
                <SectionTitle>Dates et horaires</SectionTitle>
              </SectionHeader>
              <Grid>
                <FormGroup>
                  <Label htmlFor="main_date">
                    Date de l'événement physique <RequiredStar>*</RequiredStar>
                  </Label>
                  <Input 
                    id="main_date"
                    type="date" 
                    ref={main_date_ref} 
                    value={mainDate}
                    onChange={(e) => setMainDate(e.target.value)}
                    required 
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="online_date">
                    Date de l'événement en ligne {eventType !== 'fd100' && <RequiredStar>*</RequiredStar>}
                  </Label>
                  <Input
                    id="online_date"
                    type="date"
                    ref={online_date_ref}
                    value={onlineDate}
                    onChange={(e) => setOnlineDate(e.target.value)}
                    required={eventType !== 'fd100'}
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="am_or_pm">
                    Horaires de l'événement {eventType !== 'fd100' && <RequiredStar>*</RequiredStar>}
                  </Label>
                  <Select
                    id="am_or_pm"
                    ref={am_or_pm_ref}
                    value={amOrPm}
                    onChange={(e) => setAmOrPm(e.target.value)}
                    required={eventType !== 'fd100'}
                  >
                    <option value="">-- Sélectionner --</option>
                    <option value="am">Matin (9h - 13h)</option>
                    <option value="pm">Après-midi (14h - 18h)</option>
                  </Select>
                </FormGroup>
              </Grid>
              <Grid style={{ marginTop: theme.spacing.lg }}>
                <FormGroup>
                  <Label htmlFor="inscriptions_start">
                    Date de début des inscriptions {eventType !== 'fd100' && <RequiredStar>*</RequiredStar>}
                  </Label>
                  <Input
                    id="inscriptions_start"
                    type="date"
                    ref={inscriptions_start_date_ref}
                    value={inscriptionsStartDate}
                    onChange={(e) => setInscriptionsStartDate(e.target.value)}
                    required={eventType !== 'fd100'}
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="inscriptions_end">
                    Date de fin des inscriptions {eventType !== 'fd100' && <RequiredStar>*</RequiredStar>}
                  </Label>
                  <Input
                    id="inscriptions_end"
                    type="date"
                    ref={inscriptions_end_date_ref}
                    value={inscriptionsEndDate}
                    onChange={(e) => setInscriptionsEndDate(e.target.value)}
                    required={eventType !== 'fd100'}
                  />
                </FormGroup>
              </Grid>
            </Card>

            {/* SDE Configuration Section — only for SDE/SDO events */}
            {(['sde', 'sdo'].includes(eventType)) && (
              <Card>
                <SectionHeader>
                  <SectionIcon>⚡</SectionIcon>
                  <SectionTitle>Configuration Speed Dating</SectionTitle>
                </SectionHeader>
                <Grid>
                  <FormGroup>
                    <Label>Inscriptions Pop A (Entreprises)</Label>
                    <Select value={popAOpen ? 'true' : 'false'} onChange={(e) => setPopAOpen(e.target.value === 'true')}>
                      <option value="true">Ouvertes ✅</option>
                      <option value="false">Fermées ❌</option>
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label>Inscriptions Pop B (Designers)</Label>
                    <Select value={popBOpen ? 'true' : 'false'} onChange={(e) => setPopBOpen(e.target.value === 'true')}>
                      <option value="true">Ouvertes ✅</option>
                      <option value="false">Fermées ❌</option>
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label>Durée d'un créneau (minutes)</Label>
                    <Input type="number" value={slotDuration} onChange={(e) => setSlotDuration(Number(e.target.value))} min={5} max={60} />
                  </FormGroup>
                  <FormGroup>
                    <Label>Max candidatures par designer</Label>
                    <Input type="number" value={maxCandidatures} onChange={(e) => setMaxCandidatures(Number(e.target.value))} min={1} max={30} />
                  </FormGroup>
                </Grid>
                <FormGroup style={{ marginTop: theme.spacing.lg }}>
                  <Label>Créneaux de rencontre</Label>
                  <small style={{ color: theme.colors.text.tertiary, marginBottom: theme.spacing.sm }}>
                    Définissez les créneaux horaires. Cochez "En ligne" pour les créneaux virtuels.
                  </small>
                  {sdeSlots.map((slot, idx) => (
                    <div key={slot.id} style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, marginBottom: theme.spacing.sm }}>
                      <Input
                        style={{ flex: 1 }}
                        value={slot.label}
                        onChange={(e) => {
                          const updated = [...sdeSlots];
                          updated[idx] = { ...updated[idx], label: e.target.value };
                          setSdeSlots(updated);
                        }}
                        placeholder={`Créneau ${idx + 1}`}
                      />
                      <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                        <input
                          type="checkbox"
                          checked={slot.online}
                          onChange={(e) => {
                            const updated = [...sdeSlots];
                            updated[idx] = { ...updated[idx], online: e.target.checked };
                            setSdeSlots(updated);
                          }}
                        />
                        En ligne
                      </label>
                      <button
                        type="button"
                        onClick={() => setSdeSlots(sdeSlots.filter((_, i) => i !== idx))}
                        style={{ background: 'none', border: 'none', color: theme.colors.error, cursor: 'pointer', fontSize: '1.1rem', padding: '4px' }}
                        title="Supprimer ce créneau"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    style={{ marginTop: theme.spacing.sm }}
                    onClick={() => setSdeSlots([...sdeSlots, { id: `slot${Date.now()}`, label: '', online: false }])}
                  >
                    + Ajouter un créneau
                  </Button>
                </FormGroup>
              </Card>
            )}

            {/* Media Section */}
            <Card>
              <SectionHeader>
                <SectionIcon>🖼️</SectionIcon>
                <SectionTitle>Médias</SectionTitle>
              </SectionHeader>
              <Grid>
                <FormGroup>
                  <Label htmlFor="logo">
                    Logo de l'événement <RequiredStar>*</RequiredStar>
                  </Label>
                  <Input
                    id="logo"
                    type="file"
                    ref={logo_path_ref}
                    onChange={handleLogoUpload}
                    required={!isEditMode}
                    accept="image/*"
                  />
                  <small style={{ color: theme.colors.text.tertiary, marginTop: theme.spacing.xs }}>
                    Format recommandé: PNG, JPG (max 2MB)
                  </small>
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="image">
                    Visuel principal <RequiredStar>*</RequiredStar>
                  </Label>
                  <Input
                    id="image"
                    type="file"
                    ref={image_path_ref}
                    onChange={handleImageUpload}
                    required={!isEditMode}
                    accept="image/*"
                  />
                  <small style={{ color: theme.colors.text.tertiary, marginTop: theme.spacing.xs }}>
                    Format recommandé: JPG, PNG (max 5MB)
                  </small>
                </FormGroup>
              </Grid>
            </Card>

            <ActionBar>
              <Button variant="outline" onClick={() => navigate("/admin/events")}>
                Annuler
              </Button>
              <Button type="submit" disabled={loading || loadingEvent}>
                {loading && <LoadingSpinner />}
                {loading
                  ? (isEditMode ? 'Enregistrement...' : 'Création en cours...')
                  : (isEditMode ? 'Enregistrer les modifications' : 'Créer l\'événement')}
              </Button>
            </ActionBar>
          </form>
        </FormContainer>

        {/* Live Preview Sidebar */}
        <PreviewCard>
          <PreviewTitle>📋 Aperçu en temps réel</PreviewTitle>
          
          {/* Progress Indicator */}
          <div style={{ marginBottom: theme.spacing.lg }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing.sm }}>
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: theme.colors.text.secondary }}>
                Progression
              </span>
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: theme.colors.primary }}>
                {calculateProgress()}%
              </span>
            </div>
            <ProgressBar>
              <ProgressFill progress={calculateProgress()} />
            </ProgressBar>
          </div>

          <PreviewSection eventType={eventType}>
            <PreviewItem>
              <strong>Type d'événement</strong>
              <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                <span style={{ fontSize: '1.2rem' }}>{getEventTypeInfo(eventType).icon}</span>
                <span style={{ 
                  fontWeight: '500', 
                  color: theme.colors.eventTypes[eventType]?.primary,
                  background: 'transparent !important',
                  backgroundColor: 'transparent !important',
                  padding: '0',
                  borderRadius: '0'
                }}>{getEventTypeName()}</span>
              </div>
            </PreviewItem>
          </PreviewSection>

          <PreviewItem>
            <strong>Nom de l'événement</strong>
            <span>{titleFR || 'Non défini'}</span>
          </PreviewItem>
          
          <PreviewItem>
            <strong>Localisation</strong>
            <span>{localisation || 'Non définie'}</span>
          </PreviewItem>
          
          <PreviewItem>
            <strong>Date physique</strong>
            <span>{formatDate(mainDate)}</span>
          </PreviewItem>
          
          <PreviewItem>
            <strong>Date en ligne</strong>
            <span>{formatDate(onlineDate)}</span>
          </PreviewItem>
          
          <PreviewItem>
            <strong>Horaires</strong>
            <span>
              {amOrPm === 'am' ? 'Matin (9h-13h)' : 
               amOrPm === 'pm' ? 'Après-midi (14h-18h)' : 
               'Non défini'}
            </span>
          </PreviewItem>
          
          <PreviewItem>
            <strong>Période d'inscriptions</strong>
            <span>
              Du {formatDate(inscriptionsStartDate)} au {formatDate(inscriptionsEndDate)}
            </span>
          </PreviewItem>

          {(['sde', 'sdo'].includes(eventType)) && (
            <PreviewItem>
              <strong>Config Speed Dating</strong>
              <span style={{ fontSize: '0.85rem' }}>
                Pop A: {popAOpen ? '✅ Ouvert' : '❌ Fermé'} | Pop B: {popBOpen ? '✅ Ouvert' : '❌ Fermé'}
                <br/>
                {sdeSlots.length} créneaux · {slotDuration}min · Max {maxCandidatures} candidatures
              </span>
            </PreviewItem>
          )}

          <PreviewItem>
            <strong>Médias</strong>
            <MediaPreviewContainer>
              <MediaPreviewItem>
                <MediaFileName>Logo: {logoFile?.name || 'Non sélectionné'}</MediaFileName>
                {logoPreview ? (
                  <MediaPreviewImage src={logoPreview} alt="Logo preview" />
                ) : (
                  <MediaPreviewPlaceholder>
                    📷<br/>Aucun logo<br/>sélectionné
                  </MediaPreviewPlaceholder>
                )}
              </MediaPreviewItem>
              
              <MediaPreviewItem>
                <MediaFileName>Visuel: {imageFile?.name || 'Non sélectionné'}</MediaFileName>
                {imagePreview ? (
                  <MediaPreviewImage src={imagePreview} alt="Image preview" />
                ) : (
                  <MediaPreviewPlaceholder>
                    🖼️<br/>Aucun visuel<br/>sélectionné
                  </MediaPreviewPlaceholder>
                )}
              </MediaPreviewItem>
            </MediaPreviewContainer>
          </PreviewItem>
          
          <Alert style={{ marginTop: theme.spacing.lg }}>
            💡 <strong>Conseil:</strong> Vérifiez que les dates d'inscription précèdent bien les dates d'événement.
          </Alert>

          {calculateProgress() === 100 && (
            <Alert variant="success" style={{ marginTop: theme.spacing.md }}>
              ✅ <strong>Formulaire complet!</strong> Vous pouvez maintenant créer l'événement.
            </Alert>
          )}
        </PreviewCard>
      </MainLayout>
    </Container>
  );
};

export default CreateEventModern;